import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cn from 'classnames';
import {
  dayIsAvailable,
  disableLeftArrow,
  disableRightArrow,
  equalDates,
  getDaysInMonth,
  organizeDaysIntoWeeks,
  weekdays,
  monthNames,
  generateTimeOptions,
} from './calendarActions';
import styles from './customCalendar.module.scss';

type CustomCalendarProps = {
  currentDate: Date | null;
  setCurrentDate: Function;
  min?: Date;
  max: Date;
  hasTodayBtn: boolean;
  hasTime: boolean;
  autoEnter?: boolean;
  setTime?: Function;
};

const CustomCalendar = observer(
  ({
    currentDate,
    setCurrentDate,
    min = new Date(),
    max,
    hasTodayBtn,
    hasTime,
    setTime,
    autoEnter,
  }: CustomCalendarProps) => {
    const [calendarMonth, setCalendarMonth] = useState(
      (currentDate || new Date()).getMonth(),
    );

    const [calendarYear, setCalendarYear] = useState(
      (currentDate || new Date()).getFullYear(),
    );

    const days = getDaysInMonth(calendarMonth, calendarYear);
    const weeks = organizeDaysIntoWeeks(days);

    useEffect(() => {
      if (autoEnter) {
        const selectedTime = generateTimeOptions(
          currentDate,
          min,
          max,
        )[0].split(':');
        (setTime as Function)({
          hours: selectedTime[0],
          minutes: selectedTime[1],
        });
      }
    }, [autoEnter]);

    const setDate = (date: Date) => {
      if (
        !!currentDate &&
        date.getMonth() === currentDate.getMonth() &&
        date.getFullYear() === currentDate.getFullYear() &&
        date.getDate() === currentDate.getDate()
      ) {
        return setCurrentDate(null);
      }
      if (date.getMonth() !== calendarMonth) {
        setCalendarMonth(date.getMonth());
      }
      if (date.getFullYear() !== calendarYear) {
        setCalendarYear(date.getFullYear());
      }
      if (
        date.getMonth() === min.getMonth() &&
        date.getFullYear() === min.getFullYear() &&
        date.getDate() === min.getDate()
      ) {
        const availDate = new Date(
          date.setHours(
            min.getHours(),
            Math.ceil(min.getMinutes() / 15) * 15,
            min.getSeconds(),
          ),
        );
        return setCurrentDate(availDate);
      }
      return setCurrentDate(date);
    };

    const changeMonth = (increment: number) => {
      const newMonth = calendarMonth + increment;
      if (newMonth < 0) {
        setCalendarMonth(11);
        setCalendarYear(calendarYear - 1);
      } else if (newMonth > 11) {
        setCalendarMonth(0);
        setCalendarYear(calendarYear + 1);
      } else {
        setCalendarMonth(newMonth);
      }
    };
    const changeYear = (increment: number) => {
      setCalendarYear(calendarYear + increment);
    };

    const handleTimeChange = (event: any) => {
      const selectedTime = event.target.value.split(':');
      (setTime as Function)({
        hours: selectedTime[0],
        minutes: selectedTime[1],
      });
    };

    return (
      <div className={styles.calendar}>
        <div className={styles.calendarContainer}>
          <div className={styles.calendarRow}>
            <div className={styles.calendarHeader}>
              <div className={styles.navigateIcons}>
                <button
                    type="button"
                  disabled={
                    !disableRightArrow(
                      min,
                      new Date(calendarYear - 1, calendarMonth, 1),
                    )
                  }
                  className={styles.prevYearIcon}
                  onClick={() => changeYear(-1)}
                />
                <button
                    type="button"
                  disabled={
                    !disableRightArrow(
                      min,
                      new Date(calendarYear, calendarMonth - 1, 1),
                    )
                  }
                  className={styles.prevMonthIcon}
                  onClick={() => changeMonth(-1)}
                />
              </div>
              <div className={styles.calendarMonthYear}>
                <div className={styles.calendarMonth}>
                  <span>{monthNames[calendarMonth]}</span>
                </div>
                <div className={styles.calendarYear}>
                  <span>{calendarYear}</span>
                </div>
              </div>
              <div className={styles.navigateIcons}>
                <button
                    type="button"
                  disabled={
                    !disableLeftArrow(
                      max,
                      new Date(calendarYear, calendarMonth + 1, 1),
                    )
                  }
                  className={styles.nextMonthIcon}
                  onClick={() => changeMonth(1)}
                />
                <button
                    type="button"
                  disabled={
                    !disableLeftArrow(
                      max,
                      new Date(calendarYear + 1, calendarMonth, 1),
                    )
                  }
                  className={styles.nextYearIcon}
                  onClick={() => changeYear(1)}
                />
              </div>
            </div>
          </div>
          <div className={styles.calendarRow}>
            {weekdays.map((day: any, index: any) => (
              <div
                key={`${day}_${index}`}
                className={cn(styles.calendarCell, styles.header)}
              >
                {day}
              </div>
            ))}
          </div>
          {weeks.length &&
            weeks.map((week: any, i: any) => (
              <div key={i} className={styles.calendarRow}>
                {week.map(
                  (day: any, j: any) =>
                    !!day && (
                      <button
                          type="button"
                        onClick={() => {
                          setDate(day.date);
                        }}
                        key={`${day.date}_${j}`}
                        disabled={!dayIsAvailable(min, max, day.date)}
                        className={cn(styles.calendarCell, styles.withPointer, {
                          [styles.now]: equalDates(day.date, new Date()),
                          [styles.otherMonth]:
                            day.date.getMonth() !== calendarMonth,
                          [styles.magenta]:
                            currentDate && equalDates(day.date, currentDate),
                        })}
                      >
                        {day.date.getDate()}
                      </button>
                    ),
                )}
              </div>
            ))}
        </div>
        {hasTodayBtn && (
          <div
            onClick={() => {
              setCalendarYear(new Date().getFullYear());
              setCalendarMonth(new Date().getMonth());
            }}
            className={styles.today}
          >
            Today
          </div>
        )}
        {hasTime && currentDate && (
          <div className={styles.timeContainer}>
            <select
              defaultValue={`${currentDate.getHours()}:${Math.ceil(currentDate.getMinutes() / 15) * 15}`}
              onChange={handleTimeChange}
              className={styles.timeInput}
            >
              {generateTimeOptions(currentDate, min, max).map(
                (time: any, index: any) => (
                  <option key={index} value={time}>
                    {time}
                  </option>
                ),
              )}
            </select>
          </div>
        )}
      </div>
    );
  },
);
export default CustomCalendar;
